import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PRODUCTS } from "../../data/products";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZES = ["Newborn", "0–6 months", "6–12 months", "Toddler", "Child", "Teen", "Adult S", "Adult M", "Adult L", "Adult XL", "Not applicable"];

const inputCls =
  "w-full rounded-xl border border-[#E6DFD5] bg-[#FDFBF7] px-4 py-3 text-sm text-[#2C2520] outline-none transition-colors duration-300 placeholder:text-[#B4A794] focus:border-[#C88A75]";

const Field = ({ label, testId, children, optional }) => (
  <label className="block" data-testid={testId}>
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8C7A6B]">
      {label} {optional && <em className="normal-case tracking-normal text-[#B4A794]">(optional)</em>}
    </span>
    {children}
  </label>
);

export default function OrderForm({ item, onItemChange }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", size: "", yarnColor: "", quantity: "1", dateNeeded: "", notes: "" });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  const IS_NETLIFY = process.env.REACT_APP_NETLIFY === "true";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      if (IS_NETLIFY) {
        fd.append("form-name", "order-request");
        fd.append("bot-field", "");
      }
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("item", item);
      fd.append("size", form.size);
      fd.append("yarn_color", form.yarnColor);
      fd.append("quantity", form.quantity || "1");
      fd.append("date_needed", form.dateNeeded);
      fd.append("notes", form.notes);
      if (photo) fd.append("photo", photo);
      if (IS_NETLIFY) {
        const res = await fetch("/", { method: "POST", body: fd });
        if (!res.ok) throw new Error("Form submission failed");
        toast.success("Request sent! I'll be in touch soon with your quote.");
      } else {
        const res = await axios.post(`${API}/orders`, fd);
        toast.success(`Request #${res.data.order_id} sent! I'll be in touch soon with your quote.`);
      }
      setForm({ name: "", email: "", phone: "", size: "", yarnColor: "", quantity: "1", dateNeeded: "", notes: "" });
      setPhoto(null);
      setPreview(null);
      onItemChange("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="order" data-testid="order-section" className="py-20 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20 lg:px-16">
        <Reveal>
          <Eyebrow testId="order-eyebrow">Custom Order Request</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-[#2C2520] sm:text-4xl lg:text-5xl">
            Tell me what you&apos;re <span className="italic text-[#A35C45]">dreaming of.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-[#6E6259] sm:text-base">
            This lands straight in my inbox with everything I need to give you a real quote — no back-and-forth.
            A 50% deposit by e-transfer, Square or PayPal confirms your order, and the rest is due before pickup, delivery or shipping.
          </p>
          <div className="mt-8 space-y-3 rounded-2xl border border-[#E6DFD5] bg-[#FAF7F2] p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B]">Lead time</p>
            <p className="text-sm text-[#5C4D42]">Most items: <strong>1–1.5 weeks</strong> · Throw blankets: a little extra</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] pt-2">Best times for consults &amp; pickup</p>
            <p className="text-sm text-[#5C4D42]">Evenings &amp; weekends</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] pt-2">How to pay</p>
            <p className="text-sm text-[#5C4D42]">Once you approve your quote, I&apos;ll reply with simple deposit instructions — <strong>e-transfer, Square or PayPal</strong>, whichever suits you best.</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="order-form" className="rounded-2xl border border-[#E6DFD5] bg-[#FAF7F2] p-6 sm:p-8">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Your name" testId="order-field-name">
                <input required data-testid="order-form-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              </Field>
              <Field label="Email" testId="order-field-email">
                <input required type="email" data-testid="order-form-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </Field>
              <Field label="Phone" testId="order-field-phone" optional>
                <input data-testid="order-form-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(519) 555-0123" />
              </Field>
              <Field label="What can I make for you?" testId="order-field-item">
                <select required data-testid="order-form-item-select" className={inputCls} value={item} onChange={(e) => onItemChange(e.target.value)}>
                  <option value="" disabled>Choose an item…</option>
                  {PRODUCTS.map((p) => (
                    <option key={p.slug} value={p.name}>{p.name} · {p.price}</option>
                  ))}
                  <option value="Something else">Something else entirely</option>
                </select>
              </Field>
              <Field label="Size" testId="order-field-size">
                <select data-testid="order-form-size-select" className={inputCls} value={form.size} onChange={set("size")}>
                  <option value="">Select a size…</option>
                  {SIZES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Yarn colour(s)" testId="order-field-yarn">
                <input data-testid="order-form-yarn-input" className={inputCls} value={form.yarnColor} onChange={set("yarnColor")} placeholder="e.g. dusty rose & cream" />
              </Field>
              <Field label="Quantity" testId="order-field-quantity">
                <input type="number" min="1" data-testid="order-form-quantity-input" className={inputCls} value={form.quantity} onChange={set("quantity")} />
              </Field>
              <Field label="Date needed by" testId="order-field-date" optional>
                <input type="date" data-testid="order-form-date-input" className={inputCls} value={form.dateNeeded} onChange={set("dateNeeded")} />
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Anything else I should know?" testId="order-field-notes" optional>
                <textarea rows="3" data-testid="order-form-notes-input" className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Baby shower gift, matching sibling set, favourite character…" />
              </Field>
            </div>

            <div className="mt-5">
              <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8C7A6B]">
                Inspiration photo <em className="normal-case tracking-normal text-[#B4A794]">(optional)</em>
              </span>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="order-form-photo-upload"
                className="stitch-grid flex w-full items-center justify-center gap-4 rounded-xl border border-dashed border-[#C88A75]/50 bg-[#EFE6D8]/50 px-4 py-6 text-sm text-[#6E6259] transition-colors duration-300 hover:border-[#A35C45]"
              >
                {preview ? (
                  <>
                    <img src={preview} alt="Inspiration preview" data-testid="order-form-photo-preview" className="h-16 w-16 rounded-lg object-cover" />
                    <span className="text-left">{photo?.name}<br /><span className="text-xs text-[#A35C45]">Tap to change</span></span>
                  </>
                ) : (
                  <span>Tap to attach a photo — a style, a colour palette, anything that inspires you</span>
                )}
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="order-form-photo-input" />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="order-form-submit-button"
              className="mt-7 w-full rounded-full bg-[#2C2520] py-4 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-[#A35C45] disabled:opacity-60"
            >
              {sending ? "Sending your request…" : "Send my order request"}
            </button>
            <p className="mt-4 text-center text-xs leading-relaxed text-[#8C7A6B]">
              Your request goes straight to doubletreblests@gmail.com — you&apos;ll hear back with a quote and next steps.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
